import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle,
  CalendarPlus,
  CalendarClock,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { MappedConversation } from "@/lib/conversation-list-map";
import {
  bulkScheduleMessages,
  summarizeBulkResult,
} from "@/lib/bulk-conversation-operations";
import {
  ScheduleMessagesForm,
  buildSchedulePayload,
  emptyScheduleMessage,
  type DraftScheduleMessage,
} from "@/components/conversations/ScheduleMessagesForm";
import { ScheduleActionOption } from "@/components/conversations/ScheduleActionOption";

export type BulkScheduleMode =
  | "choose"
  | "schedule_close"
  | "schedule_open";

interface BulkScheduleCloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversations: MappedConversation[];
  workspaceId: string;
  onJustClose: () => void | Promise<void>;
  onJustClosePreserveAssignees?: () => void | Promise<void>;
  onCompleted: () => void;
}

export function BulkScheduleCloseDialog({
  open,
  onOpenChange,
  conversations,
  workspaceId,
  onJustClose,
  onJustClosePreserveAssignees,
  onCompleted,
}: BulkScheduleCloseDialogProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<BulkScheduleMode>("choose");
  const [messages, setMessages] = useState<DraftScheduleMessage[]>([
    emptyScheduleMessage(),
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const count = conversations.length;
  const whatsappCount = conversations.filter((c) => c.channel === "whatsapp").length;

  useEffect(() => {
    if (open) {
      setMode("choose");
      setMessages([emptyScheduleMessage()]);
    }
  }, [open]);

  const handleScheduleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildSchedulePayload(messages);
      const closeConversation = mode === "schedule_close";
      const result = await bulkScheduleMessages(conversations, payload, closeConversation);

      const summary = summarizeBulkResult(
        result,
        closeConversation ? "Agendamento e encerramento" : "Agendamento",
      );

      let description = summary.description;
      if (result.skippedNonWhatsapp > 0) {
        description += ` ${result.skippedNonWhatsapp} conversa(s) ignorada(s) (apenas WhatsApp suporta agendamento).`;
      }

      toast({
        title: summary.title,
        description,
        variant: summary.variant,
      });

      onCompleted();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Erro ao agendar",
        description: err instanceof Error ? err.message : "Falha ao agendar mensagens.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Ações em massa ({count})</DialogTitle>
          <DialogDescription>
            {whatsappCount} conversa(s) WhatsApp elegíveis para agendamento.
          </DialogDescription>
        </DialogHeader>

        {mode === "choose" && (
          <div className="grid gap-3 py-2">
            <ScheduleActionOption
              icon={CheckCircle}
              iconClassName="text-muted-foreground"
              title="Apenas encerrar"
              description="Encerra as conversas sem agendar mensagem e devolve os leads ao pool."
              onClick={() => {
                void onJustClose();
                onOpenChange(false);
              }}
            />

            {onJustClosePreserveAssignees && (
              <ScheduleActionOption
                icon={CheckCircle}
                title="Encerrar sem desatribuir"
                description="Encerra as conversas e mantém os responsáveis atribuídos."
                onClick={() => {
                  void onJustClosePreserveAssignees();
                  onOpenChange(false);
                }}
              />
            )}

            <ScheduleActionOption
              icon={CalendarPlus}
              title="Agendar e encerrar"
              description="Agenda até 5 mensagens por conversa WhatsApp e encerra agora."
              disabled={whatsappCount === 0}
              onClick={() => setMode("schedule_close")}
            />

            <ScheduleActionOption
              icon={CalendarClock}
              title="Agendar sem encerrar"
              description="Agenda mensagens e mantém as conversas abertas."
              disabled={whatsappCount === 0}
              onClick={() => setMode("schedule_open")}
            />
          </div>
        )}

        {(mode === "schedule_close" || mode === "schedule_open") && (
          <ScheduleMessagesForm
            workspaceId={workspaceId}
            messages={messages}
            onMessagesChange={setMessages}
            submitLabel={
              mode === "schedule_close" ? "Agendar e encerrar" : "Agendar"
            }
            onSubmit={handleScheduleSubmit}
            onBack={() => setMode("choose")}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
